import LinkCard from '@/components/patterns/link-card'
import SortableReorderControls from '@/components/forms/SortableReorderControls'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Typography } from '@/components/ui/typography'
import withSuspense from '@/hooks/withSuspense'
import { useIsReadOnly } from '@/lib/casl'
import { useFormContext } from '@/lib/form-contexts'
import { useCreateAlbumMutation } from '@/lib/services/albums/albums.query'
import { getErrorMessage } from '@/lib/utils'
import { closestCenter, DndContext } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useStore } from '@tanstack/react-form'
import { useLocation, useParams } from '@tanstack/react-router'
import { Images, TextAlignStart } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { applySortOrder } from './designForm.utils'
import PageAnnouncementBarLinkToggle from './PageAnnouncementBarLinkToggle'

const PREFIX = 'pages./gallery.blocks.main.0'
const gallery = (str) => `${PREFIX}.${str}`
const DROP_NAVIGATION_GUARD_MS = 150

const truncateWithEllipsis = (value = '', maxLength = 60) => {
  if (typeof value !== 'string') return ''
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value
}

const getGalleryAlbumBlock = (values) => {
  const mainBlocks = values?.pages?.['/gallery']?.blocks?.main
  const blocks = Array.isArray(mainBlocks) ? mainBlocks : []

  return blocks.find((block) => block?.type === 'album') || null
}

const getAlbumEntryId = (entry) => {
  const id = Number(entry?.album_id ?? entry?.album?.id ?? entry?.id)
  return Number.isFinite(id) ? id : null
}

const buildAlbumEntry = (createdAlbum, fallbackName) => {
  const albumId = Number(createdAlbum?.id)

  return {
    id: albumId,
    album_id: albumId,
    sort_order: 1,
    album: {
      id: albumId,
      name: createdAlbum?.name ?? fallbackName,
      description: createdAlbum?.description ?? '',
      media_count: Number(createdAlbum?.media_count ?? createdAlbum?.media?.length ?? 0),
      is_enabled: createdAlbum?.is_enabled ?? true,
    },
  }
}

const appendAlbumEntry = (items, albumEntry) =>
  applySortOrder([
    ...(Array.isArray(items) ? items : []).filter(
      (item) => getAlbumEntryId(item) !== albumEntry.album_id
    ),
    albumEntry,
  ])

const withUpdatedGalleryAlbums = (values, nextAlbums) => {
  const nextValues = structuredClone(values ?? {})
  const albumBlock = getGalleryAlbumBlock(nextValues)

  if (albumBlock) {
    albumBlock.albums = nextAlbums
  }

  return nextValues
}

const getDirtyFieldPaths = (fieldMeta, albumsPath) => {
  const normalizedAlbumsPrefixes = [`${albumsPath}[`, `${albumsPath}.`]

  return [
    ...new Set(
      Object.entries(fieldMeta ?? {})
        .filter(([, meta]) => meta?.isDirty)
        .map(([path]) =>
          path === albumsPath || normalizedAlbumsPrefixes.some((prefix) => path.startsWith(prefix))
            ? albumsPath
            : path
        )
    ),
  ]
}

function SortableAlbumCard({
  item,
  index,
  count,
  onMove,
  orgId,
  returnTo,
  shouldSuppressNavigation,
}) {
  const albumMeta = item?.album
  const albumId = Number(item?.album_id ?? albumMeta?.id ?? item?.id)
  const sortableId = String(item?.id ?? albumId)
  const albumIdParam = String(Number.isFinite(albumId) && albumId > 0 ? albumId : item?.id)
  const label = albumMeta?.name || `Album #${albumIdParam}`
  const imageCount = Number(albumMeta?.media_count ?? 0)
  const description = truncateWithEllipsis(albumMeta?.description || '', 60)
  const { isReadOnly } = useIsReadOnly()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    disabled: isReadOnly,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
  }

  const handleArrowUp = () => {
    if (isReadOnly || index <= 0) return
    onMove(index, index - 1)
  }

  const handleArrowDown = () => {
    if (isReadOnly || index >= count - 1) return
    onMove(index, index + 1)
  }

  const isNavigationSuppressed = !!shouldSuppressNavigation?.()

  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="list-none"
      onClickCapture={(event) => {
        if (!shouldSuppressNavigation?.()) return
        event.preventDefault()
        event.stopPropagation()
      }}
    >
      <LinkCard
        to="/$orgId/gallery/album/$albumId"
        params={{
          orgId: String(orgId),
          albumId: albumIdParam,
        }}
        search={{ returnTo }}
        disabled={isReadOnly || isNavigationSuppressed}
        className="w-full py-0.5"
      >
        <div className="flex min-w-0 items-center gap-2">
          <div className="px-0.5">
            <SortableReorderControls
              index={index}
              count={count}
              disabled={isReadOnly || count === 1}
              showHandle={true}
              listeners={listeners}
              transition={transition}
              onMoveUp={handleArrowUp}
              onMoveDown={handleArrowDown}
              preventLinkNavigation
            />
          </div>

          <div className="min-w-0">
            <div className="font-medium truncate">{label}</div>
            {description && (
              <div className="mt-1">
                <Typography variant="supporting" className="block truncate">
                  {description}
                </Typography>
              </div>
            )}

            <div className={description ? 'mt-3' : 'mt-1'}>
              <Typography variant="supporting" className="block">
                {imageCount} {imageCount === 1 ? 'image' : 'images'}
              </Typography>
            </div>
          </div>
        </div>
      </LinkCard>
    </li>
  )
}

const GalleryPageForm = ({ getPersistedValues = null, onRebasePersistedValues = null }) => {
  const form = useFormContext()
  const { orgId } = useParams({ strict: false })
  const location = useLocation()
  const { isReadOnly } = useIsReadOnly()
  const createAlbumMutation = useCreateAlbumMutation()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [createAlbumName, setCreateAlbumName] = useState('')
  const navGuardTimeoutRef = useRef(null)
  const [isNavigationGuardActive, setIsNavigationGuardActive] = useState(false)

  const activateNavigationGuard = () => {
    setIsNavigationGuardActive(true)

    if (navGuardTimeoutRef.current) {
      clearTimeout(navGuardTimeoutRef.current)
    }
    navGuardTimeoutRef.current = setTimeout(() => {
      setIsNavigationGuardActive(false)
      navGuardTimeoutRef.current = null
    }, DROP_NAVIGATION_GUARD_MS)
  }

  useEffect(() => {
    return () => {
      if (navGuardTimeoutRef.current) {
        clearTimeout(navGuardTimeoutRef.current)
      }
    }
  }, [])

  const albumBlockPath = useStore(form.store, (state) => {
    const mainBlocks = state.values?.pages?.['/gallery']?.blocks?.main
    const blocks = Array.isArray(mainBlocks) ? mainBlocks : []
    const albumIndex = blocks.findIndex((block) => block?.type === 'album')
    return albumIndex >= 0 ? `pages./gallery.blocks.main.${albumIndex}` : null
  })
  const albumBlock = (str) => (albumBlockPath ? `${albumBlockPath}.${str}` : null)

  const resetCreateDialog = () => {
    setIsCreateDialogOpen(false)
    setCreateAlbumName('')
  }

  const albumItems = useStore(form.store, (state) => {
    const albumsBlockValue = getGalleryAlbumBlock(state.values)
    return Array.isArray(albumsBlockValue?.albums) ? albumsBlockValue.albums : []
  })
  const albumsPath = albumBlock('albums') || 'pages./gallery.blocks.main.1.albums'
  const isAddAlbumDisabled = isReadOnly || isCreateDialogOpen || createAlbumMutation.isPending
  const returnTo = location?.href || `${location?.pathname ?? ''}${location?.searchStr ?? ''}`

  const handleCreateAlbum = async () => {
    const albumName = createAlbumName.trim()
    if (!albumName || isReadOnly) return

    try {
      const createdAlbum = await createAlbumMutation.mutateAsync({
        name: albumName,
        description: null,
        is_enabled: true,
        attach_to_default_gallery: true,
      })

      if (!createdAlbum?.id) return

      const persistedValues =
        getPersistedValues?.() ?? form.options.defaultValues ?? form.store.state.values
      const currentItems = form.getFieldValue(albumsPath) || []
      const defaultItems = getGalleryAlbumBlock(persistedValues)?.albums || []
      const albumEntry = buildAlbumEntry(createdAlbum, albumName)
      const nextAlbums = appendAlbumEntry(currentItems, albumEntry)
      const nextDefaultAlbums = appendAlbumEntry(defaultItems, albumEntry)
      const dirtyFieldPaths = getDirtyFieldPaths(form.store.state.fieldMeta, albumsPath)
      const dirtyFieldValues = dirtyFieldPaths.map((path) => [
        path,
        structuredClone(path === albumsPath ? nextAlbums : form.getFieldValue(path)),
      ])
      const nextDefaultValues = withUpdatedGalleryAlbums(persistedValues, nextDefaultAlbums)

      form.reset(nextDefaultValues)
      onRebasePersistedValues?.(nextDefaultValues)

      for (const [path, value] of dirtyFieldValues) {
        form.setFieldValue(path, value, {
          dontValidate: true,
        })
      }

      toast.success('Album created')
      resetCreateDialog()
    } catch (error) {
      toast.error('Album create failed', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <div className="flex flex-col gap-y-6 mb-4">
      <PageAnnouncementBarLinkToggle pageKey="gallery" />

      <div className="flex flex-col gap-y-4">
        <Typography variant="emphasis" className="flex items-center gap-2">
          <TextAlignStart className="size-5" /> Page Content
        </Typography>

        <form.AppField name={gallery('props.title')}>
          {(field) => (
            <field.TextInput
              label="Gallery Page Title"
              placeholder="e.g. The Design Archive, Our Creations, etc."
            />
          )}
        </form.AppField>

        <form.AppField name={gallery('props.description')}>
          {(field) => (
            <field.TextareaInput
              label="Gallery Page Description"
              placeholder="Optional text shown on the Gallery page."
              rows={3}
            />
          )}
        </form.AppField>
      </div>

      <div className="flex flex-col gap-y-4">
        <div className="flex items-center justify-between gap-3">
          <Typography variant="emphasis" className="flex items-center gap-2">
            <Images className="size-5" /> Albums
          </Typography>
          <form.AppField
            name={albumBlock('albums') || 'pages./gallery.blocks.main.1.albums'}
            mode="array"
          >
            {() => {
              return (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isAddAlbumDisabled}
                  className={
                    isAddAlbumDisabled ? 'opacity-50 pointer-events-none cursor-not-allowed' : ''
                  }
                  onClick={() => {
                    setIsCreateDialogOpen(true)
                  }}
                >
                  <span className="inline-flex items-center gap-2">
                    <span>Add Album</span>
                  </span>
                </Button>
              )
            }}
          </form.AppField>
        </div>
        {/* <Typography variant="supporting">
          Prioritize how albums are shown on the Gallery page. Sort albums to control their order.
        </Typography> */}

        <form.AppField
          name={albumBlock('albums') || 'pages./gallery.blocks.main.1.albums'}
          mode="array"
        >
          {() => {
            const items = albumItems
            const moveAlbum = (fromIndex, toIndex) => {
              const currentItems = form.getFieldValue(albumsPath) || []
              if (
                fromIndex < 0 ||
                toIndex < 0 ||
                fromIndex >= currentItems.length ||
                toIndex >= currentItems.length ||
                fromIndex === toIndex
              ) {
                return
              }

              form.setFieldValue(albumsPath, arrayMove(currentItems, fromIndex, toIndex))
            }

            if (items.length === 0) {
              return (
                <Typography variant="supporting">
                  No albums found yet. Add an album to get started.
                </Typography>
              )
            }

            return (
              <div className="flex flex-col gap-2">
                <DndContext
                  collisionDetection={closestCenter}
                  onDragEnd={({ active, over }) => {
                    if (!over || active?.id === over?.id) return
                    const currentItems = form.getFieldValue(albumsPath) || []
                    const fromIndex = currentItems.findIndex(
                      (x) => String(x.id) === String(active.id)
                    )
                    const toIndex = currentItems.findIndex((x) => String(x.id) === String(over.id))
                    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return
                    activateNavigationGuard()
                    moveAlbum(fromIndex, toIndex)
                  }}
                >
                  <SortableContext items={items.map((x) => String(x.id))}>
                    {items.map((item, index) => (
                      <SortableAlbumCard
                        key={item.id}
                        item={item}
                        index={index}
                        count={items.length}
                        orgId={orgId}
                        returnTo={returnTo}
                        onMove={moveAlbum}
                        shouldSuppressNavigation={() => isNavigationGuardActive}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            )
          }}
        </form.AppField>
      </div>

      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          if (createAlbumMutation.isPending) return
          if (!open) {
            resetCreateDialog()
            return
          }

          setIsCreateDialogOpen(true)
        }}
      >
        <DialogContent size="compact">
          <DialogHeader>
            <DialogTitle>Create album</DialogTitle>
            <DialogDescription>
              Give your gallery album a title. You can add photos and update details on the next
              screen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label className="text-sm font-medium">Album title</label>
            <Input
              value={createAlbumName}
              onChange={(event) => setCreateAlbumName(event.target.value)}
              placeholder="Album name"
              disabled={isReadOnly || createAlbumMutation.isPending}
              autoFocus
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={isReadOnly || createAlbumMutation.isPending}
              onClick={resetCreateDialog}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={
                isReadOnly || createAlbumMutation.isPending || createAlbumName.trim().length === 0
              }
              onClick={handleCreateAlbum}
            >
              {createAlbumMutation.isPending ? 'Saving...' : 'Save album'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default withSuspense(GalleryPageForm)
